import { useState, useEffect, useCallback } from 'react';
import { useApp } from '../App';
import api from '../utils/api';

const STATUS_META = {
  draft:    { label:'Draft',    bg:'#F1EFE8', color:'#5F5E5A', dot:'#BDBAB0' },
  sent:     { label:'Sent',     bg:'#EAF7F2', color:'#0F6E56', dot:'#1D9E75' },
  accepted: { label:'Accepted', bg:'#EEF3FB', color:'#1A3F7A', dot:'#3B82F6' },
  declined: { label:'Declined', bg:'#FDEDEC', color:'#C0392B', dot:'#E74C3C' },
};

const SVC_ICON = {
  'Ocean LCL':'🚢','Air Freight':'✈️',"FCL 20' DV":'📦',"FCL 40' HC":'📦',"FCL 45' HC":'📦',
};

function fmtDT(str) {
  if (!str) return null;
  const d = new Date(str);
  return {
    date: d.toLocaleDateString('en-TT',{day:'2-digit',month:'short',year:'numeric'}),
    time: d.toLocaleTimeString('en-TT',{hour:'2-digit',minute:'2-digit',hour12:true}),
    full: d.toLocaleDateString('en-TT',{day:'2-digit',month:'short',year:'numeric'})+' '+
          d.toLocaleTimeString('en-TT',{hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:true}),
  };
}
function fmtTTD(n) {
  if (n==null||n===''||isNaN(n)) return '—';
  return 'TT$'+Number(n).toLocaleString('en-TT',{minimumFractionDigits:2,maximumFractionDigits:2});
}
function fmtUSD(n) {
  if (!n||n===0||isNaN(n)) return '—';
  return '$'+Number(n).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});
}
function StatusPill({status}) {
  const m = STATUS_META[status]||STATUS_META.draft;
  return (
    <span style={{display:'inline-flex',alignItems:'center',gap:5,padding:'3px 10px',borderRadius:20,background:m.bg,color:m.color,fontSize:11,fontWeight:600}}>
      <span style={{width:6,height:6,borderRadius:'50%',background:m.dot,display:'inline-block'}}/>
      {m.label}
    </span>
  );
}
function InfoCell({label,value}) {
  return (
    <div style={{display:'flex',flexDirection:'column',gap:2}}>
      <span style={{fontSize:10,color:'#888780',textTransform:'uppercase',letterSpacing:'0.06em'}}>{label}</span>
      <span style={{fontSize:13,fontWeight:500,wordBreak:'break-word'}}>{value||'—'}</span>
    </div>
  );
}

export default function Quotes() {
  const {user,toast,setPage} = useApp();
  const isAdmin = user.role==='admin';

  const [quotes,setQuotes]           = useState([]);
  const [loading,setLoading]         = useState(true);
  const [search,setSearch]           = useState('');
  const [statusFilter,setStatus]     = useState('');
  const [staffFilter,setStaff]       = useState('');
  const [dateFrom,setDateFrom]       = useState('');
  const [dateTo,setDateTo]           = useState('');
  const [serviceFilter,setService]   = useState('');
  const [sortBy,setSortBy]           = useState('created_at');
  const [sortDir,setSortDir]         = useState('desc');
  const [staffList,setStaffList]     = useState([]);
  const [showFilters,setShowFilters] = useState(false);
  const [selected,setSelected]       = useState(null);
  const [detailOpen,setDetailOpen]   = useState(false);
  const [activeTab,setActiveTab]     = useState('charges');
  const [deleteConfirm,setDeleteConfirm] = useState(null);
  const [sendOpen,setSendOpen]       = useState(false);
  const [sendTo,setSendTo]           = useState('');
  const [sendSubject,setSendSubject] = useState('');
  const [sendBody,setSendBody]       = useState('');
  const [sending,setSending]         = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url='/quotes?x=1';
      if (statusFilter) url+=`&status=${statusFilter}`;
      if (search)       url+=`&search=${encodeURIComponent(search)}`;
      let data = await api.get(url);
      if (staffFilter)   data=data.filter(q=>q.created_by_name===staffFilter);
      if (serviceFilter) data=data.filter(q=>(q.service||'').toLowerCase().includes(serviceFilter.toLowerCase()));
      if (dateFrom)      data=data.filter(q=>q.created_at>=dateFrom);
      if (dateTo)        data=data.filter(q=>q.created_at<=dateTo+'T23:59:59');
      data.sort((a,b)=>{
        let av=a[sortBy],bv=b[sortBy];
        if(typeof av==='string')av=av.toLowerCase();
        if(typeof bv==='string')bv=bv.toLowerCase();
        return (av<bv?-1:av>bv?1:0)*(sortDir==='asc'?1:-1);
      });
      setQuotes(data);
      if(isAdmin) setStaffList([...new Set(data.map(q=>q.created_by_name).filter(Boolean))].sort());
    } catch(e){toast(e.message,'error');}
    finally{setLoading(false);}
  },[search,statusFilter,staffFilter,serviceFilter,dateFrom,dateTo,sortBy,sortDir]);

  useEffect(()=>{load();},[load]);

  const openDetail = async (q) => {
    try {
      const full = await api.get(`/quotes/${q.id}`);
      setSelected(full); setActiveTab('charges'); setDetailOpen(true);
    } catch(e){toast(e.message,'error');}
  };

  const doDelete = async (q) => {
    try {
      await api.delete(`/quotes/${q.id}`);
      toast(`${q.quote_number} deleted`,'success');
      setDeleteConfirm(null); setDetailOpen(false); setSelected(null); load();
    } catch(e){toast(e.message,'error');}
  };

  const openSend = (q) => {
    setSelected(q);
    setSendTo(q.client_email||'');
    setSendSubject(`Freight Quotation ${q.quote_number} — PAS Trinidad Limited`);
    setSendBody(`Dear ${q.client_name},\n\nPlease find attached your freight quotation ${q.quote_number}.\n\nWe look forward to your confirmation.\n\nBest regards,\nPAS Trinidad Limited`);
    setSendOpen(true);
  };

  const doSend = async () => {
    setSending(true);
    try {
      const r=await api.post(`/quotes/${selected.id}/send`,{to:sendTo,subject:sendSubject,body:sendBody});
      setSendOpen(false);
      toast(r.simulated?'Simulated — configure SMTP for real emails':`Sent to ${sendTo}`,'success');
      load();
    } catch(e){toast(e.message,'error');}
    finally{setSending(false);}
  };

  const updateStatus = async (q,status) => {
    try {
      await api.put(`/quotes/${q.id}`,{status});
      if(selected?.id===q.id) setSelected(s=>({...s,status}));
      toast('Status updated','success'); load();
    } catch(e){toast(e.message,'error');}
  };

  const downloadPDF = (q) =>
    window.open(api.url(`/quotes/${q.id}/pdf`)+'?token='+(api.getToken()||''),'_blank');

  const exportCSV = () => {
    const H=['Quote #','Client','Email','Service','Origin','CFT','Chargeable Wt','Comm. Value USD',
      'Total TTD','Brokerage TTD','Transport TTD','Status','Created By','Created At','Sent At','Validity','Notes'];
    const rows=quotes.map(q=>[q.quote_number,q.client_name,q.client_email||'',q.service,q.origin,
      q.cft||'',q.chargeable_weight_lbs||'',q.commercial_value_usd||'',q.total_ttd||'',
      q.brokerage_total_ttd||'',q.transport_total_ttd||'',q.status,q.created_by_name||'',
      fmtDT(q.created_at)?.full||'',fmtDT(q.sent_at)?.full||'',q.validity||'',(q.notes||'').replace(/,/g,';')]);
    const csv=[H,...rows].map(r=>r.map(v=>`"${v}"`).join(',')).join('\n');
    const a=document.createElement('a');
    a.href=URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
    a.download=`quotes-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  const stats={
    total:quotes.length,
    sent:quotes.filter(q=>q.status==='sent').length,
    draft:quotes.filter(q=>q.status==='draft').length,
    accepted:quotes.filter(q=>q.status==='accepted').length,
    value:quotes.filter(q=>q.status!=='draft').reduce((s,q)=>s+(parseFloat(q.total_ttd)||0),0),
  };

  const SortTh=({col,label,right})=>(
    <th onClick={()=>{setSortBy(col);setSortDir(d=>sortBy===col?(d==='asc'?'desc':'asc'):'desc');}}
      style={{cursor:'pointer',userSelect:'none',whiteSpace:'nowrap',textAlign:right?'right':'left',padding:'10px 16px',background:'#F5F5F3',borderBottom:'1px solid #E8E5DE',fontSize:11,fontWeight:600,color:'#5F5E5A',textTransform:'uppercase',letterSpacing:'0.06em'}}>
      {label}{sortBy===col?(sortDir==='asc'?' ↑':' ↓'):''}
    </th>
  );

  const lines = selected?JSON.parse(selected.lines_json||'[]'):[];
  const hasActive = search||statusFilter||staffFilter||serviceFilter||dateFrom||dateTo;

  return (
    <div style={{display:'flex',flexDirection:'column',gap:20}}>

      {/* Header */}
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexWrap:'wrap',gap:12}}>
        <div>
          <h1 style={{fontFamily:"'DM Serif Display',serif",fontSize:28,color:'#1A1A18',margin:0}}>{isAdmin?'All quotes':'My quotes'}</h1>
          <p style={{fontSize:13,color:'#888780',marginTop:3}}>{isAdmin?'Complete audit trail across all staff':'Your quotation history'}</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          {isAdmin&&<button className="btn btn-outline" onClick={exportCSV}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> Export CSV</button>}
          <button className="btn btn-primary" onClick={()=>setPage('newquote')}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg> New quote</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12}}>
        {[
          {l:'Total',val:stats.total,c:'#1A1A18',s:'quotes'},
          {l:'Sent',val:stats.sent,c:'#1D9E75',s:'delivered'},
          {l:'Draft',val:stats.draft,c:'#888780',s:'pending'},
          {l:'Accepted',val:stats.accepted,c:'#3B82F6',s:'confirmed'},
          {l:'Value',val:'TT$'+Number(stats.value).toLocaleString('en-TT',{maximumFractionDigits:0}),c:'#0D2B5E',s:'sent quotes',sm:true},
        ].map(s=>(
          <div key={s.l} style={{background:'#fff',border:'1px solid #E8E5DE',borderRadius:12,padding:'14px 16px'}}>
            <div style={{fontSize:10,color:'#888780',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:4}}>{s.l}</div>
            <div style={{fontSize:s.sm?18:26,fontWeight:700,color:s.c,lineHeight:1}}>{s.val}</div>
            <div style={{fontSize:10,color:'#BDBAB0',marginTop:3}}>{s.s}</div>
          </div>
        ))}
      </div>

      {/* Search & filters */}
      <div style={{background:'#fff',border:'1px solid #E8E5DE',borderRadius:12,padding:'14px 16px'}}>
        <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
          <div style={{flex:'1 1 220px',position:'relative'}}>
            <svg style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'#BDBAB0'}} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input style={{width:'100%',padding:'8px 12px 8px 32px',border:'1px solid #E8E5DE',borderRadius:8,fontSize:13,outline:'none',boxSizing:'border-box',fontFamily:'inherit',color:'#1A1A18'}} placeholder="Search quote #, client, service…" value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          <div style={{display:'flex',gap:4}}>
            {['','draft','sent','accepted','declined'].map(s=>(
              <button key={s||'all'} onClick={()=>setStatus(s)}
                style={{padding:'6px 12px',border:`1px solid ${statusFilter===s?'#0D2B5E':'#E8E5DE'}`,borderRadius:20,background:statusFilter===s?'#0D2B5E':'transparent',color:statusFilter===s?'#fff':'#5F5E5A',fontSize:12,fontWeight:500,cursor:'pointer',fontFamily:'inherit',transition:'all 0.15s'}}>
                {s===''?'All':STATUS_META[s].label}
              </button>
            ))}
          </div>
          <button onClick={()=>setShowFilters(f=>!f)}
            style={{display:'flex',alignItems:'center',gap:6,padding:'7px 12px',border:`1px solid ${showFilters||hasActive?'#0D2B5E':'#E8E5DE'}`,borderRadius:8,background:showFilters||hasActive?'#EEF3FB':'transparent',color:showFilters||hasActive?'#0D2B5E':'#5F5E5A',fontSize:12,fontWeight:500,cursor:'pointer',fontFamily:'inherit'}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
            Filters
          </button>
          {hasActive&&<button onClick={()=>{setSearch('');setStatus('');setStaff('');setService('');setDateFrom('');setDateTo('');}} style={{padding:'7px 12px',border:'1px solid #FADBD8',borderRadius:8,background:'#FDEDEC',color:'#C0392B',fontSize:12,fontWeight:500,cursor:'pointer',fontFamily:'inherit'}}>Clear all</button>}
        </div>
        {showFilters&&(
          <div style={{display:'flex',gap:10,flexWrap:'wrap',marginTop:12,paddingTop:12,borderTop:'1px solid #F1EFE8'}}>
            {isAdmin&&<div><div style={{fontSize:10,color:'#888780',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>Staff</div><select className="select" style={{width:155,padding:'6px 10px',fontSize:13}} value={staffFilter} onChange={e=>setStaff(e.target.value)}><option value="">All staff</option>{staffList.map(n=><option key={n}>{n}</option>)}</select></div>}
            <div><div style={{fontSize:10,color:'#888780',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>Service</div><select className="select" style={{width:140,padding:'6px 10px',fontSize:13}} value={serviceFilter} onChange={e=>setService(e.target.value)}><option value="">All</option><option value="Ocean">Ocean LCL</option><option value="Air">Air Freight</option><option value="FCL 20">FCL 20'</option><option value="FCL 40">FCL 40'</option><option value="FCL 45">FCL 45'</option></select></div>
            <div><div style={{fontSize:10,color:'#888780',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>From</div><input className="input" type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} style={{width:148,padding:'6px 10px',fontSize:13}}/></div>
            <div><div style={{fontSize:10,color:'#888780',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>To</div><input className="input" type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} style={{width:148,padding:'6px 10px',fontSize:13}}/></div>
          </div>
        )}
      </div>

      {/* Table */}
      <div style={{background:'#fff',border:'1px solid #E8E5DE',borderRadius:12,overflow:'hidden'}}>
        {loading?(
          <div style={{padding:60,textAlign:'center',color:'#888780'}}>Loading quotes…</div>
        ):quotes.length===0?(
          <div style={{padding:60,textAlign:'center'}}>
            <div style={{fontSize:36,marginBottom:12}}>📄</div>
            <div style={{fontWeight:600,fontSize:15,marginBottom:4}}>{hasActive?'No quotes match your filters':'No quotes yet'}</div>
            <div style={{fontSize:13,color:'#888780'}}>{hasActive?'Try adjusting your search':'Create your first quote to get started'}</div>
          </div>
        ):(
          <>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead>
              <tr>
                <SortTh col="quote_number" label="Quote #"/>
                <SortTh col="client_name" label="Client"/>
                <SortTh col="service" label="Service"/>
                <SortTh col="total_ttd" label="Total TTD" right/>
                <SortTh col="status" label="Status"/>
                {isAdmin&&<SortTh col="created_by_name" label="Created by"/>}
                <SortTh col="created_at" label="Created"/>
                <SortTh col="sent_at" label="Sent"/>
                <th style={{padding:'10px 16px',background:'#F5F5F3',borderBottom:'1px solid #E8E5DE',width:110}}></th>
              </tr>
            </thead>
            <tbody>
              {quotes.map(q=>{
                const created=fmtDT(q.created_at);
                const sent=fmtDT(q.sent_at);
                return (
                  <tr key={q.id} onClick={()=>openDetail(q)} style={{borderBottom:'1px solid #F1EFE8',cursor:'pointer'}}
                    onMouseEnter={e=>e.currentTarget.style.background='#FAFAF8'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{padding:'12px 16px'}}>
                      <div style={{fontFamily:"'DM Serif Display',serif",fontSize:14,fontWeight:700,color:'#0D2B5E'}}>{q.quote_number}</div>
                    </td>
                    <td style={{padding:'12px 16px'}}>
                      <div style={{fontWeight:600,fontSize:13}}>{q.client_name}</div>
                      {q.client_email&&<div style={{fontSize:11,color:'#888780',marginTop:1}}>{q.client_email}</div>}
                    </td>
                    <td style={{padding:'12px 16px'}}>
                      <div style={{display:'flex',alignItems:'center',gap:6,fontSize:13}}>
                        <span style={{fontSize:15}}>{SVC_ICON[q.service]||'📋'}</span>
                        <span style={{color:'#5F5E5A'}}>{q.service}</span>
                      </div>
                      <div style={{fontSize:11,color:'#888780',marginTop:1}}>{q.origin}</div>
                    </td>
                    <td style={{padding:'12px 16px',textAlign:'right',fontWeight:700,fontSize:13}}>{fmtTTD(q.total_ttd)}</td>
                    <td style={{padding:'12px 16px'}}><StatusPill status={q.status}/></td>
                    {isAdmin&&<td style={{padding:'12px 16px',fontSize:13,fontWeight:500}}>{q.created_by_name||'—'}</td>}
                    <td style={{padding:'12px 16px'}}>
                      {created?<><div style={{fontSize:12,fontWeight:500}}>{created.date}</div><div style={{fontSize:11,color:'#888780'}}>{created.time}</div></>:<span style={{color:'#BDBAB0',fontSize:12}}>—</span>}
                    </td>
                    <td style={{padding:'12px 16px'}}>
                      {sent?<><div style={{fontSize:12,fontWeight:500,color:'#1D9E75'}}>{sent.date}</div><div style={{fontSize:11,color:'#888780'}}>{sent.time}</div></>:<span style={{color:'#BDBAB0',fontSize:12}}>Not sent</span>}
                    </td>
                    <td style={{padding:'12px 16px'}} onClick={e=>e.stopPropagation()}>
                      <div style={{display:'flex',gap:4,justifyContent:'flex-end'}}>
                        <button title="PDF" onClick={()=>downloadPDF(q)} style={{width:30,height:30,border:'1px solid #E8E5DE',borderRadius:6,background:'transparent',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#5F5E5A'}}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                        </button>
                        <button title="Send" onClick={()=>openSend(q)} style={{width:30,height:30,border:'1px solid #1D9E75',borderRadius:6,background:'#EAF7F2',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#1D9E75'}}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                        </button>
                        <button title="Delete" onClick={()=>setDeleteConfirm(q)} style={{width:30,height:30,border:'1px solid #FADBD8',borderRadius:6,background:'#FDEDEC',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#C0392B'}}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{padding:'10px 16px',borderTop:'1px solid #F1EFE8',background:'#FAFAF8',fontSize:12,color:'#888780'}}>
            Showing {quotes.length} quote{quotes.length!==1?'s':''}{hasActive?' (filtered)':''}
          </div>
          </>
        )}
      </div>

      {/* DETAIL MODAL */}
      {detailOpen&&selected&&!sendOpen&&!deleteConfirm&&(
        <div style={{position:'fixed',inset:0,background:'rgba(13,43,94,0.45)',backdropFilter:'blur(3px)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center',padding:16}} onClick={e=>e.target===e.currentTarget&&setDetailOpen(false)}>
          <div style={{background:'#fff',borderRadius:16,width:700,maxHeight:'90vh',display:'flex',flexDirection:'column',boxShadow:'0 24px 64px rgba(0,0,0,0.2)',overflow:'hidden'}}>
            {/* Header */}
            <div style={{background:'#0D2B5E',padding:'20px 24px',flexShrink:0}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12}}>
                <div>
                  <div style={{fontFamily:"'DM Serif Display',serif",fontSize:22,color:'#fff'}}>{selected.quote_number}</div>
                  <div style={{fontSize:13,color:'rgba(255,255,255,0.55)',marginTop:3}}>{selected.client_name} · {selected.service} · {selected.origin}</div>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <StatusPill status={selected.status}/>
                  <button onClick={()=>setDetailOpen(false)} style={{width:30,height:30,borderRadius:8,background:'rgba(255,255,255,0.1)',border:'none',color:'rgba(255,255,255,0.8)',cursor:'pointer',fontSize:18,display:'flex',alignItems:'center',justifyContent:'center'}}>×</button>
                </div>
              </div>
              <div style={{display:'flex',gap:10,marginTop:14,flexWrap:'wrap'}}>
                {[{l:'Total TTD',v:fmtTTD(selected.total_ttd),hi:true},{l:'Total USD',v:fmtUSD(selected.total_usd)},{l:'ROE',v:'TT$'+parseFloat(selected.rate_of_exchange||6.77).toFixed(2)},{l:'Validity',v:selected.validity||'—'}].map(item=>(
                  <div key={item.l} style={{background:item.hi?'rgba(29,158,117,0.25)':'rgba(255,255,255,0.08)',borderRadius:8,padding:'8px 14px',minWidth:90}}>
                    <div style={{fontSize:10,color:'rgba(255,255,255,0.5)',textTransform:'uppercase',letterSpacing:'0.06em'}}>{item.l}</div>
                    <div style={{fontSize:14,fontWeight:700,color:'#fff',marginTop:2}}>{item.v}</div>
                  </div>
                ))}
              </div>
            </div>
            {/* Tabs */}
            <div style={{display:'flex',borderBottom:'1px solid #E8E5DE',background:'#FAFAF8',padding:'0 24px',flexShrink:0}}>
              {[['charges','Charges'],['details','Shipment details'],...(isAdmin?[['audit','Audit trail']]:[] )].map(([id,label])=>(
                <button key={id} onClick={()=>setActiveTab(id)} style={{padding:'10px 16px',border:'none',background:'none',fontFamily:'inherit',fontSize:13,cursor:'pointer',borderBottom:activeTab===id?'2px solid #0D2B5E':'2px solid transparent',color:activeTab===id?'#0D2B5E':'#888780',fontWeight:activeTab===id?600:400,marginBottom:-1}}>
                  {label}
                </button>
              ))}
            </div>
            {/* Content */}
            <div style={{flex:1,overflowY:'auto',padding:'20px 24px'}}>

              {activeTab==='charges'&&(
                <div>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                    <thead>
                      <tr style={{background:'#0D2B5E'}}>
                        {['Description','USD','TTD'].map((h,i)=>(
                          <th key={h} style={{padding:'9px 12px',color:'#fff',fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.05em',textAlign:i>0?'right':'left'}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((l,i)=>l.header?(
                        <tr key={i}><td colSpan={3} style={{background:'#F1EFE8',color:'#5F5E5A',fontWeight:700,fontSize:10,textTransform:'uppercase',letterSpacing:'0.08em',padding:'7px 12px'}}>{l.description.replace(/──\s*/g,'').replace(/\s*──/g,'')}</td></tr>
                      ):(
                        <tr key={i} style={{background:i%2===0?'#fff':'#FAFAF8',borderBottom:'1px solid #F1EFE8'}}>
                          <td style={{padding:'9px 12px'}}>{l.description}</td>
                          <td style={{padding:'9px 12px',textAlign:'right',color:'#5F5E5A'}}>{l.usd!=null&&l.usd!==''?(parseFloat(l.usd)===0?'—':'$'+parseFloat(l.usd).toFixed(2)):'—'}</td>
                          <td style={{padding:'9px 12px',textAlign:'right',fontWeight:500}}>{l.ttd!=null&&l.ttd!==''?(parseFloat(l.ttd)===0?'—':'TT$'+parseFloat(l.ttd).toFixed(2)):'—'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{background:'#1D9E75'}}>
                        <td style={{padding:'11px 12px',fontWeight:700,color:'#fff'}}>Total</td>
                        <td style={{padding:'11px 12px',textAlign:'right',fontWeight:700,color:'#fff'}}>{fmtUSD(selected.total_usd)}</td>
                        <td style={{padding:'11px 12px',textAlign:'right',fontWeight:700,color:'#fff',fontSize:14}}>{fmtTTD(selected.total_ttd)}</td>
                      </tr>
                    </tfoot>
                  </table>
                  {(selected.includes_brokerage||selected.includes_transport)&&(
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginTop:14}}>
                      {selected.includes_brokerage&&<div style={{background:'#EEF3FB',borderRadius:10,padding:'12px 16px'}}><div style={{fontSize:10,color:'#888780',textTransform:'uppercase',letterSpacing:'0.06em'}}>Brokerage</div><div style={{fontWeight:700,fontSize:16,color:'#0D2B5E',marginTop:3}}>{fmtTTD(selected.brokerage_total_ttd)}</div></div>}
                      {selected.includes_transport&&<div style={{background:'#EAF7F2',borderRadius:10,padding:'12px 16px'}}><div style={{fontSize:10,color:'#888780',textTransform:'uppercase',letterSpacing:'0.06em'}}>Transport</div><div style={{fontWeight:700,fontSize:16,color:'#1D9E75',marginTop:3}}>{fmtTTD(selected.transport_total_ttd)}</div></div>}
                    </div>
                  )}
                  {selected.notes&&<div style={{marginTop:14,background:'#F5F5F3',borderRadius:8,padding:'12px 14px',fontSize:13,color:'#5F5E5A',borderLeft:'3px solid #BDBAB0'}}><strong>Notes:</strong> {selected.notes}</div>}
                </div>
              )}

              {activeTab==='details'&&(
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  {[['Client',selected.client_name],['Client email',selected.client_email],['Service',selected.service],['Origin',selected.origin],['Commodity',selected.commodity],['Validity',selected.validity],['Rate of exchange','TT$'+parseFloat(selected.rate_of_exchange||6.77).toFixed(2)],['Commercial value',fmtUSD(selected.commercial_value_usd)],['CIF value',fmtTTD(selected.cif_value)],['CFT',selected.cft?parseFloat(selected.cft).toFixed(3):null],['Actual weight',selected.actual_weight_lbs?parseFloat(selected.actual_weight_lbs).toFixed(2)+' lbs':null],['Chargeable weight',selected.chargeable_weight_lbs?parseFloat(selected.chargeable_weight_lbs).toFixed(2)+' lbs':null],['Dimensions',selected.length_in?`${selected.length_in}" × ${selected.width_in}" × ${selected.height_in}"`:null],['Qty',selected.qty],['Brokerage',selected.includes_brokerage?'Included':'Not included'],['Transport',selected.includes_transport?`Zone ${selected.transport_zone}`:'Not included']].map(([k,v])=>(
                    <div key={k} style={{background:'#F5F5F3',borderRadius:8,padding:'10px 14px'}}><InfoCell label={k} value={v||'—'}/></div>
                  ))}
                </div>
              )}

              {activeTab==='audit'&&isAdmin&&(
                <div style={{display:'flex',flexDirection:'column',gap:12}}>
                  <div style={{position:'relative',paddingLeft:28}}>
                    <div style={{position:'absolute',left:10,top:8,bottom:8,width:2,background:'#E8E5DE',borderRadius:2}}/>
                    {[
                      {icon:'📝',color:'#EEF3FB',border:'#C7D9F8',label:'Quote created',time:selected.created_at,by:selected.created_by_name,detail:`${selected.service} · ${selected.origin}`},
                      selected.sent_at?{icon:'📧',color:'#EAF7F2',border:'#B7E4D0',label:'Sent to client',time:selected.sent_at,by:selected.created_by_name,detail:`To: ${selected.client_email||'—'}`}:null,
                      selected.updated_at&&selected.updated_at!==selected.created_at?{icon:'✏️',color:'#FFF8EC',border:'#FFD580',label:'Last updated',time:selected.updated_at,by:selected.created_by_name,detail:`Status: ${selected.status}`}:null,
                    ].filter(Boolean).map((ev,i)=>{
                      const dt=fmtDT(ev.time);
                      return (
                        <div key={i} style={{position:'relative',marginBottom:12}}>
                          <div style={{position:'absolute',left:-22,top:12,width:12,height:12,borderRadius:'50%',background:ev.border,border:'2px solid #fff',boxShadow:`0 0 0 2px ${ev.border}`}}/>
                          <div style={{background:ev.color,border:`1px solid ${ev.border}`,borderRadius:10,padding:'12px 16px',display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12}}>
                            <div style={{display:'flex',gap:10,alignItems:'flex-start'}}>
                              <span style={{fontSize:18,lineHeight:1}}>{ev.icon}</span>
                              <div>
                                <div style={{fontWeight:600,fontSize:13}}>{ev.label}</div>
                                <div style={{fontSize:12,color:'#5F5E5A',marginTop:2}}>{ev.detail}</div>
                                <div style={{fontSize:11,color:'#888780',marginTop:2}}>by <strong>{ev.by||'Unknown'}</strong></div>
                              </div>
                            </div>
                            <div style={{textAlign:'right',flexShrink:0}}>
                              <div style={{fontSize:12,fontWeight:600}}>{dt?.date}</div>
                              <div style={{fontSize:11,color:'#888780'}}>{dt?.time}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{background:'#F5F5F3',borderRadius:10,padding:16}}>
                    <div style={{fontSize:11,fontWeight:700,color:'#5F5E5A',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:12}}>Complete record</div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                      {[['Quote #',selected.quote_number],['DB ID','#'+selected.id],['Created by',selected.created_by_name],['Created at',fmtDT(selected.created_at)?.full],['Sent at',fmtDT(selected.sent_at)?.full||'Not sent'],['Last updated',fmtDT(selected.updated_at)?.full],['Status',selected.status],['Client',selected.client_name],['Email',selected.client_email||'—'],['Service',selected.service],['Origin',selected.origin],['Validity',selected.validity],['ROE','TT$'+parseFloat(selected.rate_of_exchange||6.77).toFixed(2)],['Comm. value',fmtUSD(selected.commercial_value_usd)],['CIF value',fmtTTD(selected.cif_value)],['Total USD',fmtUSD(selected.total_usd)],['Total TTD',fmtTTD(selected.total_ttd)],['Brokerage',selected.includes_brokerage?fmtTTD(selected.brokerage_total_ttd):'—'],['Transport',selected.includes_transport?fmtTTD(selected.transport_total_ttd):'—'],['CFT',selected.cft?parseFloat(selected.cft).toFixed(4):'—'],['Chargeable wt',selected.chargeable_weight_lbs?parseFloat(selected.chargeable_weight_lbs).toFixed(2)+' lbs':'—']].map(([k,v])=>(
                        <div key={k} style={{background:'#fff',borderRadius:6,padding:'8px 10px'}}><InfoCell label={k} value={v||'—'}/></div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Footer */}
            <div style={{padding:'14px 24px',borderTop:'1px solid #E8E5DE',background:'#FAFAF8',display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0,gap:10,flexWrap:'wrap'}}>
              <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                {isAdmin&&['draft','sent','accepted','declined'].map(s=>(
                  <button key={s} onClick={()=>updateStatus(selected,s)}
                    style={{padding:'5px 12px',border:`1px solid ${selected.status===s?'#0D2B5E':'#E8E5DE'}`,borderRadius:20,background:selected.status===s?'#0D2B5E':'transparent',color:selected.status===s?'#fff':'#5F5E5A',fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:'inherit',textTransform:'capitalize',transition:'all 0.15s'}}>
                    {s}
                  </button>
                ))}
              </div>
              <div style={{display:'flex',gap:8}}>
                <button className="btn btn-outline btn-sm" onClick={()=>downloadPDF(selected)}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> PDF</button>
                <button className="btn btn-green btn-sm" onClick={()=>openSend(selected)}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg> Send</button>
                <button onClick={()=>setDeleteConfirm(selected)} style={{padding:'6px 14px',border:'1px solid #FADBD8',borderRadius:6,background:'#FDEDEC',color:'#C0392B',fontSize:13,fontWeight:500,cursor:'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',gap:6}}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg> Delete</button>
                <button className="btn btn-outline btn-sm" onClick={()=>setDetailOpen(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SEND MODAL */}
      {sendOpen&&selected&&(
        <div style={{position:'fixed',inset:0,background:'rgba(13,43,94,0.45)',backdropFilter:'blur(3px)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:16}} onClick={e=>e.target===e.currentTarget&&setSendOpen(false)}>
          <div style={{background:'#fff',borderRadius:16,width:520,boxShadow:'0 24px 64px rgba(0,0,0,0.2)',overflow:'hidden'}}>
            <div style={{background:'#0D2B5E',padding:'18px 24px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontFamily:"'DM Serif Display',serif",fontSize:18,color:'#fff'}}>Send quotation</div>
              <div style={{fontSize:13,color:'rgba(255,255,255,0.5)'}}>{selected.quote_number}</div>
            </div>
            <div style={{padding:'20px 24px',display:'flex',flexDirection:'column',gap:14}}>
              <div className="field" style={{marginBottom:0}}><label className="label">To</label><input className="input" type="email" value={sendTo} onChange={e=>setSendTo(e.target.value)} placeholder="client@email.com"/></div>
              <div className="field" style={{marginBottom:0}}><label className="label">Subject</label><input className="input" value={sendSubject} onChange={e=>setSendSubject(e.target.value)}/></div>
              <div className="field" style={{marginBottom:0}}><label className="label">Message</label><textarea className="textarea" value={sendBody} onChange={e=>setSendBody(e.target.value)} rows={6}/></div>
              <div style={{background:'#F5F5F3',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#888780',display:'flex',gap:8,alignItems:'center'}}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg> PDF will be automatically attached</div>
            </div>
            <div style={{padding:'14px 24px',borderTop:'1px solid #E8E5DE',display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button className="btn btn-outline" onClick={()=>setSendOpen(false)}>Cancel</button>
              <button className="btn btn-green" onClick={doSend} disabled={sending||!sendTo}>{sending?'Sending…':'Send email'}</button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      {deleteConfirm&&(
        <div style={{position:'fixed',inset:0,background:'rgba(13,43,94,0.45)',backdropFilter:'blur(3px)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center',padding:16}} onClick={e=>e.target===e.currentTarget&&setDeleteConfirm(null)}>
          <div style={{background:'#fff',borderRadius:16,width:420,boxShadow:'0 24px 64px rgba(0,0,0,0.2)',overflow:'hidden'}}>
            <div style={{padding:'24px 24px 0'}}>
              <div style={{width:48,height:48,borderRadius:12,background:'#FDEDEC',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:14}}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#C0392B" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
              </div>
              <div style={{fontFamily:"'DM Serif Display',serif",fontSize:20,marginBottom:8}}>Delete this quote?</div>
              <p style={{fontSize:14,color:'#5F5E5A',lineHeight:1.6}}>You are about to permanently delete <strong>{deleteConfirm.quote_number}</strong> for <strong>{deleteConfirm.client_name}</strong>.</p>
              <div style={{fontSize:13,color:'#C0392B',background:'#FDEDEC',borderRadius:8,padding:'10px 12px',marginTop:10}}>⚠️ This cannot be undone.</div>
            </div>
            <div style={{padding:'20px 24px',display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button className="btn btn-outline" onClick={()=>setDeleteConfirm(null)}>Cancel</button>
              <button onClick={()=>doDelete(deleteConfirm)} style={{padding:'8px 20px',background:'#C0392B',color:'#fff',border:'none',borderRadius:8,fontSize:14,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>Delete permanently</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
